import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ClipboardList, Search, Calendar, ChevronDown, ChevronUp,
  Pencil, Copy, Trash2, Send, Mail, LogOut, Check, X,
} from "lucide-react";
import AssessmentResultsView from "../components/AssessmentResultsView";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const METRIC_NAMES = {
  sj: "Squat Jump", vj: "Vertical Jump", bj: "Broad Jump", rsi: "RSI", ag: "Approach Gain",
};

const SPORTS = [
  "Basketball", "Volleyball", "Football", "Soccer", "Baseball/Softball",
  "Track & Field", "Tennis", "Swimming", "Lacrosse", "Hockey", "Cheerleading",
  "Gymnastics", "Wrestling", "Cross Country", "Other",
];
const GRADES = [
  "5th", "6th", "7th", "8th", "9th", "10th", "11th", "12th", "college", "pro",
];

export default function AssessmentDashboard() {
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingReport, setEditingReport] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await fetch(`${API}/jump-assessment/auth/me`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCoach(data);
        fetchReports();
      } else {
        navigate("/assessments/login", { replace: true });
      }
    } catch {
      navigate("/assessments/login", { replace: true });
    }
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(`${API}/jump-assessment/reports`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (err) {
      console.error("Failed to fetch reports", err);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await fetch(`${API}/jump-assessment/auth/logout`, { method: "POST", credentials: "include" });
    navigate("/assessments/login", { replace: true });
  };

  const hasCap = (cap) => coach?.capabilities?.includes(cap);

  // CRUD actions
  const resendReport = async (assessmentId) => {
    setSendingId(assessmentId);
    try {
      const res = await fetch(`${API}/jump-assessment/send-report`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ assessment_id: assessmentId, access_token: "paladin-maxsc-2026", resend: true }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) { toast.success(data.message || "Report sent!"); fetchReports(); }
      else toast.error(data.detail || "Failed to send");
    } catch { toast.error("Failed to send report"); }
    finally { setSendingId(null); }
  };

  const duplicateReport = async (assessmentId) => {
    try {
      const res = await fetch(`${API}/jump-assessment/${assessmentId}/duplicate`, {
        method: "POST", credentials: "include",
      });
      if (res.ok) { toast.success("Assessment duplicated"); fetchReports(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || "Duplicate failed"); }
    } catch { toast.error("Failed to duplicate"); }
  };

  const deleteReport = async (assessmentId) => {
    if (!window.confirm("Delete this assessment? This cannot be undone.")) return;
    try {
      const res = await fetch(`${API}/jump-assessment/${assessmentId}`, {
        method: "DELETE", credentials: "include",
      });
      if (res.ok) { toast.success("Assessment deleted"); setExpandedId(null); fetchReports(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || "Delete failed"); }
    } catch { toast.error("Failed to delete"); }
  };

  const saveEdit = async () => {
    if (!editingReport) return;
    try {
      const res = await fetch(`${API}/jump-assessment/${editingReport.assessment_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(editingReport),
      });
      if (res.ok) { toast.success("Assessment updated"); setEditingReport(null); fetchReports(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || "Update failed"); }
    } catch { toast.error("Failed to update"); }
  };

  const filteredReports = reports.filter((r) =>
    !searchQuery ||
    (r.athlete_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.parent_name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
    (r.sport || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getDomColor = (val) => {
    if (val >= 100) return "text-green-400";
    if (val >= 75) return "text-amber-400";
    return "text-red-400";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#070B14] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#070B14]" data-testid="assessment-dashboard">
      {/* Header */}
      <header className="border-b border-[#1E293B] bg-[#0f172a]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-black tracking-wider text-white">
              <span className="text-blue-500">PALADIN</span> ASSESSMENTS
            </h1>
            <p className="text-xs text-slate-500">{coach?.email}</p>
          </div>
          <button
            onClick={logout}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="assessment-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search by athlete name, parent, or sport..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#141B2D] border border-[#1E293B] rounded-lg pl-10 pr-4 py-2.5 text-white text-sm focus:border-blue-500 outline-none"
            data-testid="search-reports"
          />
        </div>

        {/* Results */}
        {filteredReports.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No assessments found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredReports.map((report) => {
              const expanded = expandedId === report.assessment_id;
              const isEditing = editingReport?.assessment_id === report.assessment_id;
              return (
                <div
                  key={report.assessment_id}
                  className="bg-[#141B2D] border border-[#1E293B] rounded-xl overflow-hidden"
                  data-testid={`report-${report.assessment_id}`}
                >
                  {/* Summary Row */}
                  <button
                    onClick={() => { setExpandedId(expanded ? null : report.assessment_id); setEditingReport(null); }}
                    className="w-full flex items-center gap-3 p-4 sm:p-5 text-left hover:bg-[#1E293B]/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-white text-base md:text-lg">{report.athlete_name}</span>
                        <span className="text-sm text-slate-300 capitalize">{report.age_grade} &middot; {report.sport}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        <span className="text-sm font-semibold text-emerald-300">Stage: {report.stage_score}%</span>
                        <span className="text-sm font-semibold text-blue-300">Elite: {report.elite_score}%</span>
                        {report.primary_limiter && (
                          <span className="text-sm font-semibold text-red-400">Limiter: {METRIC_NAMES[report.primary_limiter] || report.primary_limiter}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <div className="text-right hidden sm:block">
                        <p className="text-sm text-slate-300">
                          <Calendar className="w-3.5 h-3.5 inline mr-1" />
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                        {report.email_sent && <p className="text-xs font-semibold text-green-400 mt-0.5">Email sent</p>}
                      </div>
                      {expanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </button>

                  {/* Expanded Detail */}
                  {expanded && (
                    <div className="border-t border-[#1E293B] p-3 sm:p-4 space-y-4 bg-[#0f172a]/50">
                      {isEditing ? (
                        <EditForm data={editingReport} onChange={setEditingReport} onSave={saveEdit} onCancel={() => setEditingReport(null)} />
                      ) : (
                        <AssessmentResultsView report={report} />
                      )}

                      {/* Actions — capability-gated */}
                      {!isEditing && (
                        <div className="flex flex-wrap gap-2">
                          {hasCap("edit") && (
                            <button onClick={() => setEditingReport({ ...report })}
                              className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`edit-${report.assessment_id}`}>
                              <Pencil className="w-3 h-3" /> Edit
                            </button>
                          )}
                          {hasCap("duplicate") && (
                            <button onClick={() => duplicateReport(report.assessment_id)}
                              className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                              data-testid={`duplicate-${report.assessment_id}`}>
                              <Copy className="w-3 h-3" /> Duplicate
                            </button>
                          )}
                          <button onClick={() => resendReport(report.assessment_id)}
                            disabled={sendingId === report.assessment_id}
                            className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                            data-testid={`resend-${report.assessment_id}`}>
                            <Send className="w-3 h-3" />
                            {sendingId === report.assessment_id ? "Sending..." : report.email_sent ? "Re-send Email" : "Send Email"}
                          </button>
                          <a href={`mailto:${report.parent_email}`}
                            className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                            <Mail className="w-3 h-3" /> Email Parent
                          </a>
                          {hasCap("delete") && (
                            <button onClick={() => deleteReport(report.assessment_id)}
                              className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ml-auto"
                              data-testid={`delete-${report.assessment_id}`}>
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

/* Inline Edit Form */
function EditForm({ data, onChange, onSave, onCancel }) {
  const update = (field, value) => onChange({ ...data, [field]: value });
  return (
    <div className="space-y-3" data-testid="edit-form">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <Field label="Athlete Name" value={data.athlete_name} onChange={(v) => update("athlete_name", v)} />
        <Field label="Parent Name" value={data.parent_name} onChange={(v) => update("parent_name", v)} />
        <Field label="Parent Email" value={data.parent_email} onChange={(v) => update("parent_email", v)} type="email" />
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Gender</label>
          <select value={data.gender || "male"} onChange={(e) => update("gender", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            <option value="male">Male</option><option value="female">Female</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Grade</label>
          <select value={data.age_grade} onChange={(e) => update("age_grade", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-slate-500 mb-0.5">Sport</label>
          <select value={data.sport} onChange={(e) => update("sport", e.target.value)}
            className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none">
            {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <Field label="Bodyweight" value={data.bodyweight} onChange={(v) => update("bodyweight", parseFloat(v) || 0)} type="number" />
        <Field label="Height (in)" value={data.height_inches} onChange={(v) => update("height_inches", parseFloat(v) || 0)} type="number" />
        <Field label="Squat Jump" value={data.squat_jump} onChange={(v) => update("squat_jump", parseFloat(v) || 0)} type="number" />
        <Field label="Vertical Jump" value={data.vertical_jump} onChange={(v) => update("vertical_jump", parseFloat(v) || 0)} type="number" />
        <Field label="Broad Jump" value={data.broad_jump} onChange={(v) => update("broad_jump", parseFloat(v) || 0)} type="number" />
        <Field label="RSI" value={data.rsi} onChange={(v) => update("rsi", parseFloat(v) || 0)} type="number" />
        <Field label="Approach Jump" value={data.approach_jump} onChange={(v) => update("approach_jump", parseFloat(v) || 0)} type="number" />
        <Field label="Reach (in)" value={data.reach_inches || ""} onChange={(v) => update("reach_inches", parseFloat(v) || null)} type="number" />
        <Field label="Touch (in)" value={data.approach_touch_inches || ""} onChange={(v) => update("approach_touch_inches", parseFloat(v) || null)} type="number" />
        <Field label="Iso Squat" value={data.iso_squat_force || ""} onChange={(v) => update("iso_squat_force", parseFloat(v) || null)} type="number" />
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={onSave}
          className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          data-testid="save-edit-btn">
          <Check className="w-3.5 h-3.5" /> Save & Recalculate
        </button>
        <button onClick={onCancel}
          className="flex items-center gap-1.5 bg-[#1E293B] hover:bg-[#2D3B4F] text-white px-4 py-2 rounded-lg text-xs font-medium transition-colors"
          data-testid="cancel-edit-btn">
          <X className="w-3.5 h-3.5" /> Cancel
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-[10px] text-slate-500 mb-0.5">{label}</label>
      <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0f172a] border border-[#1E293B] rounded px-2 py-1.5 text-white text-xs outline-none focus:border-blue-500"
        step={type === "number" ? "0.01" : undefined}
      />
    </div>
  );
}
